export type Locale = "ru" | "en";

export type Translation = {
  documentTitle: string;
  header: {
    features: string;
    device: string;
    restaurants: string;
    integrations: string;
    flex: string;
    contacts: string;
    discussPilot: string;
    language: string;
  };
  hero: {
    eyebrow: string;
    titleLines: [string, string, string];
    lead: string;
    requestDemo: string;
    watchScenario: string;
    chipTablet: string;
    chipPay: string;
    chipCustomize: string;
    cardMenuTitle: string;
    cardMenuText: string;
    cardOrderTitle: string;
    cardOrderText: string;
    cardPayTitle: string;
    cardPayText: string;
  };
  device: {
    eyebrow: string;
    titleLines: [string, string];
    lead: string;
    restaurantAlt: string;
    restaurantCaption: string;
    menuAlt: string;
    menuCaption: string;
    gamesAlt: string;
    gamesCaption: string;
  };
  benefits: {
    eyebrow: string;
    titleLines: [string, string];
    lead: string;
    items: Array<{ icon: string; title: string; text: string }>;
    hoverHint: string;
    steakAlt: string;
    steakName: string;
    steakPicked: string;
    steakPrice: string;
    yourOrder: string;
    steakOrderCount: string;
    placeOrder: string;
    paymentSuccess: string;
    paymentThanks: string;
    flowNoteBefore: string;
    flowNoteSteps: string;
    addAria: string;
  };
  integrations: {
    eyebrow: string;
    titleLines: [string, string];
    lead: string;
    groups: Array<{
      icon: string;
      title: string;
      text: string;
      items: Array<{ name: string; logo: string; suffix?: string }>;
    }>;
  };
  flex: {
    eyebrow: string;
    titleLines: [string, string];
    lead: string;
    cards: Array<{ icon: string; title: string; text: string }>;
    steps: Array<{ title: string; text: string }>;
    ctaTitle: string;
    ctaText: string;
    discussPilot: string;
    getPresentation: string;
  };
  contact: {
    eyebrow: string;
    title: string;
    lead: string;
    name: string;
    namePlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    comment: string;
    commentPlaceholder: string;
    submit: string;
    submitting: string;
    success: string;
    error: string;
    consentBefore: string;
    consentLink: string;
    consentAfter: string;
    consentRequired: string;
  };
  tablet: {
    menu: string;
    catalog: string;
    tabs: string[];
    gridItems: Array<{ title: string; price: string; image: string }>;
    listItems: Array<{
      title: string;
      description: string;
      price: string;
      image: string;
    }>;
    cart: string;
    cartTotal: string;
    addAria: string;
  };
};

export const translations: Record<Locale, Translation> = {
  ru: {
    documentTitle: "Table One — заказ и оплата за столом",
    header: {
      features: "Возможности",
      device: "Устройство",
      restaurants: "Для ресторанов",
      integrations: "Интеграции",
      flex: "Гибкость",
      contacts: "Контакты",
      discussPilot: "Обсудить пилот",
      language: "Язык",
    },
    hero: {
      eyebrow: "Для ресторанов",
      titleLines: ["Заказ и оплата", "за столом —", "без ожидания"],
      lead:
        "Гость выбирает блюда на планшете, отправляет заказ на кухню и оплачивает картой. Ресторан получает более удобный сервис и понятный формат для пилота.",
      requestDemo: "Запросить демо",
      watchScenario: "Посмотреть сценарий",
      chipTablet: "Планшет\nна каждом столе",
      chipPay: "Оплата\nкартой",
      chipCustomize: "Доработка\nпод клиента",
      cardMenuTitle: "Меню",
      cardMenuText: "Гость выбирает блюда на планшете",
      cardOrderTitle: "Заказ",
      cardOrderText: "Заказ уходит на кухню",
      cardPayTitle: "Оплата",
      cardPayText: "Оплата картой за пару секунд",
    },
    device: {
      eyebrow: "В зале ресторана",
      titleLines: ["Table One", "в ресторане"],
      lead:
        "Table One — это физическое устройство для зала ресторана. Его можно установить на столы, чтобы гость видел меню, оформлял заказ и переходил к оплате в одном понятном сценарии.",
      restaurantAlt: "Планшет Table One на сервированном столе в интерьере ресторана",
      restaurantCaption: "Вписывается в интерьер ресторана",
      menuAlt: "Меню с фотографиями блюд на планшете Table One за столом",
      menuCaption: "Меню перед гостем",
      gamesAlt: "Экран с мини-играми на планшете Table One",
      gamesCaption: "Игры, пока готовится заказ",
    },
    benefits: {
      eyebrow: "Для ресторанов",
      titleLines: ["Почему Table One", "удобен ресторану"],
      lead:
        "Сервис помогает гостю сделать заказ и оплатить его прямо за столом. Команда тратит меньше времени на рутину, а ресторан получает понятный инструмент для пилота и дальнейшего роста.",
      items: [
        {
          icon: "⚡",
          title: "Быстрее обслуживание",
          text: "Заказ без ожидания официанта.",
        },
        {
          icon: "☷",
          title: "Меньше рутинных действий",
          text: "Меньше лишних шагов для команды.",
        },
        {
          icon: "▭",
          title: "Удобная оплата",
          text: "Гость оплачивает как ему удобно.",
        },
        {
          icon: "↗",
          title: "Готовность к пилоту",
          text: "Легко запустить и масштабировать.",
        },
      ],
      hoverHint: "Наведите на экран, чтобы увидеть!",
      steakAlt: "Стейк Рибай",
      steakName: "Стейк Рибай",
      steakPicked: "Гость выбрал блюдо",
      steakPrice: "1 890 ₽",
      yourOrder: "Ваш заказ",
      steakOrderCount: "1 позиция в заказе",
      placeOrder: "Оформить заказ",
      paymentSuccess: "Оплата прошла успешно",
      paymentThanks: "Спасибо! Ждём вас снова.",
      flowNoteBefore: "При наведении заказ проходит путь:",
      flowNoteSteps: "выбор → заказ → оплата",
      addAria: "Добавить",
    },
    integrations: {
      eyebrow: "Интеграции",
      titleLines: ["Работаем с тем, что уже", "стоит в ресторане"],
      lead:
        "Планшет подключается к действующей кассе и программе лояльности, а гость платит привычным способом — картой или по QR СБП. Ничего менять в учёте не нужно.",
      groups: [
        {
          icon: "▤",
          title: "Касса",
          text: "Заказ с планшета попадает в кассу заведения как обычный заказ стола, оплата закрывает его там же — с фискальным чеком кассы.",
          items: [
            { name: "iiko", logo: "/pictures/logos/iiko.svg" },
            { name: "r_keeper", logo: "/pictures/logos/rkeeper.svg" },
          ],
        },
        {
          icon: "◈",
          title: "Программа лояльности",
          text: "Гость видит баллы и списывает их прямо на планшете; начисление и списание проходят через лояльность заведения.",
          items: [
            { name: "iikoCard", logo: "/pictures/logos/iiko.svg", suffix: "Card" },
            { name: "Mindbox", logo: "/pictures/logos/mindbox.svg" },
          ],
        },
        {
          icon: "▣",
          title: "Оплата",
          text: "Карта прикладывается к планшету — терминал не нужен; по QR СБП гость платит из приложения своего банка.",
          items: [
            { name: "Мир", logo: "/pictures/logos/mir.svg" },
            { name: "Visa", logo: "/pictures/logos/visa.svg" },
            { name: "Mastercard", logo: "/pictures/logos/mastercard.svg" },
            { name: "СБП", logo: "/pictures/logos/sbp.svg", suffix: "СБП" },
          ],
        },
      ],
    },
    flex: {
      eyebrow: "Гибкость и поддержка",
      titleLines: ["Готовы адаптировать решение", "под формат ресторана"],
      lead:
        "Начинаем с пилота на нескольких столах и при необходимости дорабатываем интерфейс, меню и сценарии оплаты под задачи клиента.",
      cards: [
        {
          icon: "▤",
          title: "Подключение к действующей кассе",
          text: "Помогаем связать планшет с текущей системой ресторана.",
        },
        {
          icon: "🍴",
          title: "Настройка меню и оплаты",
          text: "Адаптируем каталог, оплату и сервисные сценарии.",
        },
        {
          icon: "□",
          title: "Акции и бонусы по запросу",
          text: "При необходимости добавляем маркетинговые механики.",
        },
        {
          icon: "▥",
          title: "Отчёты и уведомления",
          text: "Показываем ключевые события и результаты пилота.",
        },
      ],
      steps: [
        {
          title: "Обсуждаем задачи",
          text: "Понимаем цели и особенности вашего ресторана.",
        },
        {
          title: "Настраиваем решение",
          text: "Адаптируем меню, оплату и интерфейс под задачи.",
        },
        {
          title: "Запускаем в ресторане",
          text: "Подключаем планшеты и обучаем команду.",
        },
        {
          title: "Масштабируем дальше",
          text: "Расширяем на новые зоны и заведения сети.",
        },
      ],
      ctaTitle: "Запустите Table One в пилотном ресторане",
      ctaText: "Начните с нескольких планшетов и проверьте эффект на практике.",
      discussPilot: "Обсудить пилот",
      getPresentation: "Получить презентацию",
    },
    contact: {
      eyebrow: "Заявка на пилот",
      title: "Обсудим Table One для вашего ресторана",
      lead:
        "Оставьте контакты — мы свяжемся, обсудим формат ресторана, пилот и возможные доработки под ваши задачи.",
      name: "Имя",
      namePlaceholder: "Например, Александр",
      phone: "Телефон",
      phonePlaceholder: "+7 999 123-45-67",
      comment: "Комментарий",
      commentPlaceholder: "Например: хотим пилот на 5 столов, ресторан в Москве",
      submit: "Отправить заявку",
      submitting: "Отправляем...",
      success: "Заявка отправлена. Скоро свяжемся!",
      error: "Не получилось отправить заявку. Попробуйте ещё раз.",
      consentBefore: "Согласен с ",
      consentLink: "политикой обработки персональных данных",
      consentAfter: "",
      consentRequired: "Без согласия на обработку данных заявку отправить нельзя.",
    },
    tablet: {
      menu: "Меню",
      catalog: "Каталог меню",
      tabs: ["Популярное", "Салаты", "Супы", "Горячее", "Десерты", "Напитки"],
      gridItems: [
        {
          title: "Лосось с овощами",
          price: "1 290 ₽",
          image: "/pictures/images/fish.jpg",
        },
        {
          title: "Том Ям",
          price: "690 ₽",
          image: "/pictures/images/soup.png",
        },
        {
          title: "Поке с тунцом",
          price: "1 190 ₽",
          image: "/pictures/images/poke.png",
        },
        {
          title: "Цыплёнок табака",
          price: "1 480 ₽",
          image: "/pictures/images/chiken.png",
        },
      ],
      listItems: [
        {
          title: "Стейк Рибай",
          description: "с овощами",
          price: "1 890 ₽",
          image: "/pictures/images/steak.png",
        },
        {
          title: "Завтрак",
          description: "с яйцом и зеленью",
          price: "780 ₽",
          image: "/pictures/images/breakfast.png",
        },
        {
          title: "Том Ям",
          description: "с креветками",
          price: "690 ₽",
          image: "/pictures/images/soup.png",
        },
        {
          title: "Салат с курицей",
          description: "с лёгким соусом",
          price: "760 ₽",
          image: "/pictures/images/salad.png",
        },
        {
          title: "Лосось с овощами",
          description: "на горячем",
          price: "1 290 ₽",
          image: "/pictures/images/fish.jpg",
        },
      ],
      cart: "🛒 В корзине · 3 блюда",
      cartTotal: "3 870 ₽",
      addAria: "Добавить",
    },
  },
  en: {
    documentTitle: "Table One — order and pay at the table",
    header: {
      features: "Features",
      device: "Device",
      restaurants: "For restaurants",
      integrations: "Integrations",
      flex: "Flexibility",
      contacts: "Contact",
      discussPilot: "Discuss a pilot",
      language: "Language",
    },
    hero: {
      eyebrow: "For restaurants",
      titleLines: ["Order and pay", "at the table —", "with no waiting"],
      lead:
        "Guests choose dishes on a tablet, send the order to the kitchen, and pay by card. The restaurant gets a smoother guest experience and a clear format for a pilot.",
      requestDemo: "Request a demo",
      watchScenario: "See the flow",
      chipTablet: "A tablet\nat every table",
      chipPay: "Card\npayments",
      chipCustomize: "Customized\nfor you",
      cardMenuTitle: "Menu",
      cardMenuText: "Guests browse dishes on the tablet",
      cardOrderTitle: "Order",
      cardOrderText: "The order goes to the kitchen",
      cardPayTitle: "Payment",
      cardPayText: "Pay by card in seconds",
    },
    device: {
      eyebrow: "At the restaurant",
      titleLines: ["Table One", "at the table"],
      lead:
        "Table One is a physical device for the dining room. Place it on tables so guests can browse the menu, place an order, and pay in one clear flow.",
      restaurantAlt: "Table One tablet on a set table inside a restaurant",
      restaurantCaption: "At home in your restaurant",
      menuAlt: "A menu with dish photos on a Table One tablet at the table",
      menuCaption: "The menu, right in front of you",
      gamesAlt: "Mini-games displayed on a Table One tablet",
      gamesCaption: "Games while your order is cooking",
    },
    benefits: {
      eyebrow: "For restaurants",
      titleLines: ["Why Table One", "works for restaurants"],
      lead:
        "The product helps guests order and pay right at the table. The team spends less time on routine work, and the restaurant gets a clear tool for a pilot and further growth.",
      items: [
        {
          icon: "⚡",
          title: "Faster service",
          text: "Guests order without waiting for a waiter.",
        },
        {
          icon: "☷",
          title: "Less routine work",
          text: "Fewer extra steps for the team.",
        },
        {
          icon: "▭",
          title: "Convenient payments",
          text: "Guests pay the way that suits them.",
        },
        {
          icon: "↗",
          title: "Ready for a pilot",
          text: "Easy to launch and scale.",
        },
      ],
      hoverHint: "Hover over the screen to see it!",
      steakAlt: "Ribeye steak",
      steakName: "Ribeye steak",
      steakPicked: "The guest chose a dish",
      steakPrice: "$21",
      yourOrder: "Your order",
      steakOrderCount: "1 item in the order",
      placeOrder: "Place order",
      paymentSuccess: "Payment successful",
      paymentThanks: "Thank you! See you again.",
      flowNoteBefore: "On hover, the order moves through:",
      flowNoteSteps: "choose → order → pay",
      addAria: "Add",
    },
    integrations: {
      eyebrow: "Integrations",
      titleLines: ["Works with what the", "restaurant already runs"],
      lead:
        "The tablet connects to the existing POS and loyalty program, and guests pay the way they are used to — by card or via SBP QR. Nothing changes in the accounting.",
      groups: [
        {
          icon: "▤",
          title: "POS",
          text: "An order from the tablet lands in the restaurant's POS as a regular table order, and the payment closes it right there — with the POS fiscal receipt.",
          items: [
            { name: "iiko", logo: "/pictures/logos/iiko.svg" },
            { name: "r_keeper", logo: "/pictures/logos/rkeeper.svg" },
          ],
        },
        {
          icon: "◈",
          title: "Loyalty program",
          text: "Guests see their points and redeem them on the tablet; earning and redemption go through the restaurant's own loyalty system.",
          items: [
            { name: "iikoCard", logo: "/pictures/logos/iiko.svg", suffix: "Card" },
            { name: "Mindbox", logo: "/pictures/logos/mindbox.svg" },
          ],
        },
        {
          icon: "▣",
          title: "Payments",
          text: "A card is tapped on the tablet itself — no separate terminal; with SBP QR the guest pays from their banking app.",
          items: [
            { name: "Mir", logo: "/pictures/logos/mir.svg" },
            { name: "Visa", logo: "/pictures/logos/visa.svg" },
            { name: "Mastercard", logo: "/pictures/logos/mastercard.svg" },
            { name: "SBP", logo: "/pictures/logos/sbp.svg", suffix: "SBP" },
          ],
        },
      ],
    },
    flex: {
      eyebrow: "Flexibility and support",
      titleLines: ["Ready to adapt the product", "to your restaurant format"],
      lead:
        "We start with a pilot on a few tables and, if needed, customize the interface, menu, and payment flow for your goals.",
      cards: [
        {
          icon: "▤",
          title: "Connect to your POS",
          text: "We help link the tablet to your current restaurant system.",
        },
        {
          icon: "🍴",
          title: "Menu and payment setup",
          text: "We adapt the catalog, payments, and service scenarios.",
        },
        {
          icon: "□",
          title: "Promos and bonuses on request",
          text: "We can add marketing mechanics when you need them.",
        },
        {
          icon: "▥",
          title: "Reports and alerts",
          text: "We surface key events and pilot results.",
        },
      ],
      steps: [
        {
          title: "Discuss goals",
          text: "We learn your restaurant’s needs and constraints.",
        },
        {
          title: "Configure the solution",
          text: "We adapt the menu, payments, and interface.",
        },
        {
          title: "Launch in the restaurant",
          text: "We install tablets and train the team.",
        },
        {
          title: "Scale further",
          text: "We expand to new zones and locations.",
        },
      ],
      ctaTitle: "Launch Table One in a pilot restaurant",
      ctaText: "Start with a few tablets and see the impact in practice.",
      discussPilot: "Discuss a pilot",
      getPresentation: "Get the deck",
    },
    contact: {
      eyebrow: "Pilot request",
      title: "Let’s discuss Table One for your restaurant",
      lead:
        "Leave your contacts — we’ll reach out to talk through your restaurant format, the pilot, and any custom work you need.",
      name: "Name",
      namePlaceholder: "For example, Alex",
      phone: "Phone",
      phonePlaceholder: "+1 555 123-4567",
      comment: "Comment",
      commentPlaceholder: "For example: pilot on 5 tables, restaurant in NYC",
      submit: "Send request",
      submitting: "Sending...",
      success: "Request sent. We’ll be in touch soon!",
      error: "Couldn’t send the request. Please try again.",
      consentBefore: "I agree to the ",
      consentLink: "personal data processing policy",
      consentAfter: "",
      consentRequired: "We cannot accept the request without your consent to data processing.",
    },
    tablet: {
      menu: "Menu",
      catalog: "Menu catalog",
      tabs: ["Popular", "Salads", "Soups", "Mains", "Desserts", "Drinks"],
      gridItems: [
        {
          title: "Salmon with vegetables",
          price: "$14",
          image: "/pictures/images/fish.jpg",
        },
        {
          title: "Tom Yum",
          price: "$8",
          image: "/pictures/images/soup.png",
        },
        {
          title: "Tuna poke",
          price: "$13",
          image: "/pictures/images/poke.png",
        },
        {
          title: "Chicken tabaka",
          price: "$16",
          image: "/pictures/images/chiken.png",
        },
      ],
      listItems: [
        {
          title: "Ribeye steak",
          description: "with vegetables",
          price: "$21",
          image: "/pictures/images/steak.png",
        },
        {
          title: "Breakfast",
          description: "with egg and greens",
          price: "$9",
          image: "/pictures/images/breakfast.png",
        },
        {
          title: "Tom Yum",
          description: "with shrimp",
          price: "$8",
          image: "/pictures/images/soup.png",
        },
        {
          title: "Chicken salad",
          description: "with a light dressing",
          price: "$8",
          image: "/pictures/images/salad.png",
        },
        {
          title: "Salmon with vegetables",
          description: "served hot",
          price: "$14",
          image: "/pictures/images/fish.jpg",
        },
      ],
      cart: "🛒 In cart · 3 dishes",
      cartTotal: "$43",
      addAria: "Add",
    },
  },
};

export const locales: Locale[] = ["ru", "en"];

export function isLocale(value: string | null | undefined): value is Locale {
  return value === "ru" || value === "en";
}

export function detectLocale(): Locale {
  if (typeof window === "undefined") {
    return "ru";
  }

  const saved = window.localStorage.getItem("table-one-locale");
  if (isLocale(saved)) {
    return saved;
  }

  const browser = window.navigator.language.toLowerCase();
  return browser.startsWith("en") ? "en" : "ru";
}
